import type { InboxChannel, InboxDirection, InboxKind, InboxStatus } from '../types/inbox.js';

// Community Inbox Phase 1.5 — pure view helpers (tested).

// ---------------------------------------------------------------------------
// Exact timestamps (brief F): the thread body shows the absolute date + time
// ("18 Jun 2026, 9:55 am"); relative stays in the list / tooltips.
// ---------------------------------------------------------------------------

const MONTH_ABBREV = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
] as const;

export function formatExactTimestamp(
  iso: string | null | undefined,
  opts?: { timeZone?: string },
): string {
  if (!iso) return '';
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return iso;
  // Compose from numeric parts (locale-independent — ICU's en-AU "short"
  // month is unabbreviated for some months, e.g. "June").
  const parts = new Intl.DateTimeFormat('en-AU', {
    day: 'numeric',
    month: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: opts?.timeZone,
  }).formatToParts(new Date(ms));
  const get = (type: string): string => parts.find((p) => p.type === type)?.value ?? '';
  const monthIdx = Number(get('month')) - 1;
  const month = MONTH_ABBREV[monthIdx] ?? get('month');
  const dayPeriod = get('dayPeriod').toLowerCase().replace(/\./g, '');
  return `${get('day')} ${month} ${get('year')}, ${get('hour')}:${get('minute')} ${dayPeriod}`;
}

// ---------------------------------------------------------------------------
// Post media (Phase 1.5 preview panel): postImageUrl may be an image OR a
// video file URL — sniff by path extension (query/fragment ignored) so the
// panel can render a playable <video> for video posts.
// ---------------------------------------------------------------------------

const VIDEO_EXTENSIONS = new Set(['mp4', 'm4v', 'mov', 'webm', 'ogv']);

export function isProbablyVideoUrl(url: string | null | undefined): boolean {
  if (!url) return false;
  const path = url.split(/[?#]/, 1)[0] ?? '';
  const dot = path.lastIndexOf('.');
  if (dot === -1) return false;
  return VIDEO_EXTENSIONS.has(path.slice(dot + 1).toLowerCase());
}

// ---------------------------------------------------------------------------
// Bulk resolve (Part D, 2026-07-19) — a human-readable summary of the ACTIVE
// filters for the confirm dialog, so "resolve everything showing" states
// exactly what "showing" means before it runs.
// ---------------------------------------------------------------------------

export function describeActiveFilters(
  filters: {
    dateFrom?: string;
    dateTo?: string;
    statuses?: string[];
    channels?: string[];
    kinds?: string[];
    accountIds?: string[];
    brandAccountIds?: string[];
    accountManagerIds?: string[];
    assigneeUids?: string[];
    search?: string;
    priorityOnly?: boolean;
  },
  labels?: { accountName?: (id: string) => string | undefined },
): string[] {
  const parts: string[] = [];
  if (filters.dateFrom || filters.dateTo) {
    parts.push(
      filters.dateFrom && filters.dateTo
        ? `dated ${filters.dateFrom} to ${filters.dateTo}`
        : filters.dateFrom
          ? `dated from ${filters.dateFrom}`
          : `dated until ${filters.dateTo}`,
    );
  }
  if (filters.kinds?.length) parts.push(`kind: ${filters.kinds.join(', ')}`);
  if (filters.statuses?.length) parts.push(`status: ${filters.statuses.join(', ')}`);
  if (filters.channels?.length) parts.push(`channel: ${filters.channels.join(', ')}`);
  if (filters.accountIds?.length) {
    const names = filters.accountIds.map((id) => labels?.accountName?.(id) ?? id);
    parts.push(`account: ${names.join(', ')}`);
  }
  if (filters.brandAccountIds?.length) {
    parts.push(`${filters.brandAccountIds.length} brand filter(s)`);
  }
  if (filters.accountManagerIds?.length) {
    parts.push(`${filters.accountManagerIds.length} account-manager filter(s)`);
  }
  if (filters.assigneeUids?.length) parts.push(`${filters.assigneeUids.length} assignee filter(s)`);
  if (filters.priorityOnly) parts.push('priority only');
  if (filters.search?.trim()) parts.push(`search: "${filters.search.trim()}"`);
  return parts;
}

// ---------------------------------------------------------------------------
// Reply target (B1/B2, 2026-07-19).
//
// Under the per-comment model every thread IS a comment, which silently removed
// the ability to post a TOP-LEVEL comment on the post's main thread. The
// composer now carries an explicit target:
//   - 'comment' — reply threaded under that comment (the DEFAULT: the comment
//     you arrived from, or any other comment via its Reply action).
//   - 'post'    — a new top-level comment on the post (no replyToMessageId).
// ---------------------------------------------------------------------------

export type ReplyTarget =
  | { kind: 'comment'; threadId: string; messageId: string; authorName: string }
  | { kind: 'post' };

/**
 * The (threadId, replyToMessageId) to send for a target. A post-level comment
 * still routes through a thread on that post (the server resolves the post id
 * from it) but sends NO replyToMessageId, which is what makes it top-level.
 */
export function replySendArgs(
  target: ReplyTarget,
  fallbackThreadId: string,
): { threadId: string; replyToMessageId?: string } {
  return target.kind === 'comment'
    ? { threadId: target.threadId, replyToMessageId: target.messageId }
    : { threadId: fallbackThreadId };
}

/** A private reply (comment → DM) can only ever target a specific comment. */
export function canSendPrivately(target: ReplyTarget): boolean {
  return target.kind === 'comment';
}

// ---------------------------------------------------------------------------
// Comment "Like" capability (A2, 2026-07-19).
//
// Instagram has NO likeable-comment endpoint in Zernio — its `canLike` contract
// is explicitly "(Facebook, Twitter/X, Bluesky, Reddit)". Clicking Like on an
// IG comment therefore 502'd. Zernio returns a per-comment `canLike` on hydrated
// poll rows, which is authoritative; webhook-only comments have no flag, so we
// fall back to a per-channel rule. The control is HIDDEN when unsupported —
// never rendered as a button that errors.
// ---------------------------------------------------------------------------

/** Channels whose comments Zernio can like (see canLike contract above). */
const LIKEABLE_CHANNELS: ReadonlySet<InboxChannel> = new Set<InboxChannel>(['facebook', 'metaads']);

export function canLikeComment(
  message: { canLike?: boolean | null; direction?: InboxDirection; externalId?: string | null },
  channel: InboxChannel,
): boolean {
  // Only an inbound comment with a platform id can be moderated at all.
  if (message.direction === 'outgoing') return false;
  if (message.externalId === null) return false;
  // Zernio's per-comment flag wins when present.
  if (message.canLike === true) return true;
  if (message.canLike === false) return false;
  // Unknown (webhook-only ingest) → platform rule.
  return LIKEABLE_CHANNELS.has(channel);
}

// ---------------------------------------------------------------------------
// Webhook health (Part F, 2026-07-19). The health card moved off the page into
// the Manage-accounts dialog to reclaim vertical space; this predicate drives
// BOTH the card and the small "needs attention" dot on the Manage accounts
// button — so a dead webhook (which silently stops the whole inbox) is still
// discoverable without permanently occupying a row.
//
// Structural param (not the api.ts type) so this stays a dependency-free lib.
// ---------------------------------------------------------------------------

export function isWebhookHealthy(
  h:
    | {
        registered: boolean;
        isActive: boolean | null;
        failureCount: number | null;
        secretConfigured: boolean;
      }
    | null
    | undefined,
): boolean {
  if (!h) return true; // unknown → don't cry wolf
  return h.secretConfigured && h.registered && h.isActive === true && (h.failureCount ?? 0) === 0;
}

// ---------------------------------------------------------------------------
// Message attachment classification (Part E1): decide how to render an inbox
// attachment — an inline image (thumbnail + lightbox), an inline video, or a
// plain "other" link. Social attachments are overwhelmingly images and often
// carry a CDN url with no extension + a vague/absent type, so an untyped url
// that isn't a known non-visual kind is treated as an image (a broken one just
// falls back to the browser's placeholder — better than a bare "see attachment"
// link).
// ---------------------------------------------------------------------------

const NON_VISUAL_ATTACHMENT_TYPES = new Set(['file', 'audio', 'link', 'share', 'template', 'document']);
const IMAGE_EXT_RE = /\.(jpg|jpeg|png|gif|webp|bmp|heic|heif|avif)(\?|#|$)/i;

export function attachmentKind(
  att: { type?: string | null; url?: string | null },
): 'image' | 'video' | 'other' {
  const type = (att.type ?? '').toLowerCase().trim();
  const url = att.url ?? '';
  if (!url) return 'other';
  if (type.includes('video') || isProbablyVideoUrl(url)) return 'video';
  if (type.includes('image') || type.includes('photo') || IMAGE_EXT_RE.test(url)) return 'image';
  if (NON_VISUAL_ATTACHMENT_TYPES.has(type)) return 'other';
  // Untyped / 'unknown' social attachment with a url → assume image.
  return 'image';
}

// ---------------------------------------------------------------------------
// Channel labels (icons live in ChannelIcon.tsx; this is the shared text map).
// ---------------------------------------------------------------------------

export const CHANNEL_LABEL: Record<InboxChannel, string> = {
  facebook: 'Facebook',
  instagram: 'Instagram',
  metaads: 'Ad comment',
  googlebusiness: 'Google Business',
};

// ---------------------------------------------------------------------------
// Kind filter (brief A + §4): All / Comments / Reviews / Messages segmented
// control. "All" (the default) applies NO kind filter — a merged list of every
// kind (ThreadList already renders mixed kinds per-row). The individual tabs
// scope to one kind.
// ---------------------------------------------------------------------------

// 'all' is a UI-only sentinel (no kind filter); the persisted kinds stay the
// real InboxKind values.
export type KindSegmentValue = 'all' | InboxKind;

export const KIND_SEGMENTS: Array<{ kind: KindSegmentValue; label: string; emptyState: string }> = [
  { kind: 'all', label: 'All', emptyState: 'No items match the current filters.' },
  { kind: 'comment', label: 'Comments', emptyState: 'No comments match the current filters.' },
  { kind: 'review', label: 'Reviews', emptyState: 'No reviews match the current filters.' },
  { kind: 'dm', label: 'Messages', emptyState: 'No messages match the current filters.' },
];

const KIND_SEGMENT_STORAGE_KEY = 'kyck-inbox-kind-segment';
const VALID_KIND_SEGMENTS: ReadonlySet<string> = new Set(['all', 'comment', 'review', 'dm']);

export function normalizeKindSegment(raw: unknown): KindSegmentValue {
  return typeof raw === 'string' && VALID_KIND_SEGMENTS.has(raw)
    ? (raw as KindSegmentValue)
    : 'all';
}

export function loadKindSegment(): KindSegmentValue {
  if (typeof window === 'undefined') return 'all';
  try {
    return normalizeKindSegment(window.sessionStorage.getItem(KIND_SEGMENT_STORAGE_KEY));
  } catch {
    return 'all';
  }
}

export function saveKindSegment(kind: KindSegmentValue): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(KIND_SEGMENT_STORAGE_KEY, kind);
  } catch {
    // Session persistence is best-effort.
  }
}

// ---------------------------------------------------------------------------
// Feed sort direction (brief §D4) — pure recency; newest-first by default with
// a newest ↔ oldest toggle. Session-persisted like the other feed controls.
// ---------------------------------------------------------------------------

export type FeedSortDir = 'newest' | 'oldest';

const SORT_DIR_STORAGE_KEY = 'kyck-inbox-sort-dir';

export function normalizeSortDir(raw: unknown): FeedSortDir {
  return raw === 'oldest' ? 'oldest' : 'newest';
}

export function loadSortDir(): FeedSortDir {
  if (typeof window === 'undefined') return 'newest';
  try {
    return normalizeSortDir(window.sessionStorage.getItem(SORT_DIR_STORAGE_KEY));
  } catch {
    return 'newest';
  }
}

export function saveSortDir(dir: FeedSortDir): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(SORT_DIR_STORAGE_KEY, dir);
  } catch {
    // Session persistence is best-effort.
  }
}

// ---------------------------------------------------------------------------
// Default status filter: the working queue defaults to New + Pending — resolved
// items drop off. ('open' was removed in Part A.) The user's choice persists
// for the session; the stored value is normalized defensively.
// ---------------------------------------------------------------------------

export const DEFAULT_STATUS_FILTER: InboxStatus[] = ['new', 'pending'];

const VALID_STATUSES: ReadonlySet<string> = new Set(['new', 'pending', 'resolved']);
const STATUS_FILTER_STORAGE_KEY = 'kyck-inbox-status-filter';

/**
 * Normalize a persisted (or otherwise untrusted) status selection: drop
 * unknown values; null/invalid input → the default working set. An
 * explicitly-empty array is preserved (= "All status", including Resolved).
 */
export function normalizeStatusFilter(raw: unknown): InboxStatus[] {
  if (!Array.isArray(raw)) return [...DEFAULT_STATUS_FILTER];
  const cleaned = raw.filter(
    (v): v is InboxStatus => typeof v === 'string' && VALID_STATUSES.has(v),
  );
  return cleaned;
}

export function loadStatusFilter(): InboxStatus[] {
  if (typeof window === 'undefined') return [...DEFAULT_STATUS_FILTER];
  try {
    const raw = window.sessionStorage.getItem(STATUS_FILTER_STORAGE_KEY);
    if (raw === null) return [...DEFAULT_STATUS_FILTER];
    return normalizeStatusFilter(JSON.parse(raw));
  } catch {
    return [...DEFAULT_STATUS_FILTER];
  }
}

export function saveStatusFilter(statuses: InboxStatus[]): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(STATUS_FILTER_STORAGE_KEY, JSON.stringify(statuses));
  } catch {
    // Session persistence is best-effort.
  }
}

// ---------------------------------------------------------------------------
// Account Manager filter (brief §2) — session-persisted like the status filter.
// A plain string[] of User uids (default = none, i.e. all AMs).
// ---------------------------------------------------------------------------

const AM_FILTER_STORAGE_KEY = 'kyck-inbox-am-filter';

export function normalizeUidFilter(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter((v): v is string => typeof v === 'string' && v.trim() !== '');
}

export function loadAmFilter(): string[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.sessionStorage.getItem(AM_FILTER_STORAGE_KEY);
    if (raw === null) return [];
    return normalizeUidFilter(JSON.parse(raw));
  } catch {
    return [];
  }
}

export function saveAmFilter(uids: string[]): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(AM_FILTER_STORAGE_KEY, JSON.stringify(uids));
  } catch {
    // Session persistence is best-effort.
  }
}
